import {
    FormControlLabel, FormGroup, FormHelperText, Typography,
    Switch,
    Box,
} from '@mui/material';
import {
    WhatsApp, Email, InfoRounded, NotificationsNoneOutlined, TrendingUpRounded,
} from '@mui/icons-material';
import { useForm } from 'react-hook-form';
import React, { PropsWithChildren, useCallback } from 'react';
import { ShadowBGContainer } from '../../foundation/Containers';
import { UserNotificationProps } from '../UserNotification';
import { addNotification, removeNotification } from '../../../api/notification.api';
import { NotificationUserTypes } from '../../../../common/notification_user_type.enum';
import CustomBR from '../../foundation/CustomBR';
import { NotificationDto } from '../../../../common/notifications.types';
import { getParameterByNameSimple } from '../../../utils/urlUtils';

export const BoxedIcon = (props: {label:string } & PropsWithChildren) => (
    <Box display="flex" alignItems="center">
        {props.children}
        {props.label}
    </Box>
);

type IFormNotificationSettings = Record<string, boolean>;

export interface IUserNotificationSettingsProps {
    userId: number;
    activeNotificationSettings: Record<string, NotificationDto>;
    setNotification: React.Dispatch<React.SetStateAction<UserNotificationProps>>;
}

const icons = {
    whatsapp: {
        icon: <WhatsApp color="primary" sx={{ mr: 1, ml: 1 }} />,
    },
    email: {
        icon: <Email color="primary" sx={{ mr: 1, ml: 1 }} />,
    },
    candidate_hiring_progress: {
        icon: <TrendingUpRounded color="primary" sx={{ mr: 1, ml: 1 }} />,
    },
};

const UserNotificationSettings = (props: IUserNotificationSettingsProps) => {
    const allNotifications = Object.values(props.activeNotificationSettings).sort((a, b) => a.notificationid - b.notificationid);
    const defaultValues = allNotifications.reduce((acc, channel) => {
        acc[channel.name] = !!props.activeNotificationSettings?.[channel.name]?.isActive || false;
        return acc;
    },
    {} as IFormNotificationSettings);

    const {
        register, formState: { errors }, watch,
    } = useForm<IFormNotificationSettings>({
        defaultValues,
    });

    const hasAnyActiveChannel = allNotifications.filter(n => n.userType === NotificationUserTypes.CANDIDATE_AND_EMPLOYER)
        .some(channel => watch(channel.name));
    const formChannelRegistrations = allNotifications.filter(n => n.userType === NotificationUserTypes.CANDIDATE_AND_EMPLOYER)
        .map(channel => ({
            notification: { ...channel, description: props.activeNotificationSettings?.[channel.name]?.description },
            registration: register(channel.name),
            description: props.activeNotificationSettings?.[channel.name]?.description,
        }));
    const formNotificationsRegistrations = allNotifications.filter(n => n.userType !== NotificationUserTypes.CANDIDATE_AND_EMPLOYER)
        .map(notification => ({
            notification: { ...notification, description: props.activeNotificationSettings?.[notification.name]?.description },
            registration: register(notification.name),
        }));

    const onChange = useCallback(async (notificationId: number, value: boolean) => {
        try {
            const jwtToken = getParameterByNameSimple('token');
            if (value) {
                await addNotification({ notificationId, userid: props.userId, jwtToken });
            } else {
                await removeNotification({ notificationId, userid: props.userId, jwtToken });
            }
        } catch (error) {
            console.error('Error in onChange:', error);
            props.setNotification({
                id: 'error', message: ' שגיאה בשמירת ההגדרות בבקשה רענן את העמוד ונסה שנית', severity: 'error', blink: true,
            });
        }
    }, [props.userId, props.setNotification]);

    return (
        <>
            <ShadowBGContainer>
                <Typography color="secondary" fontWeight="bold" variant="h4">
                    <BoxedIcon label="ערוצי התראות">
                        <InfoRounded color="primary" sx={{ mr: 1, ml: 1 }} />
                    </BoxedIcon>
                </Typography>
                <CustomBR />
                {formChannelRegistrations.map(({ notification, registration }) => (
                    <FormGroup key={notification.notificationid}>
                        <FormControlLabel
                            control={<Switch
                                defaultChecked={!!props.activeNotificationSettings?.[notification?.name]?.isActive}
                                {...registration}
                                inputRef={registration.ref}
                                name={notification.name}
                                onChange={e => {
                                    registration.onChange(e);
                                    onChange(notification.notificationid, e.target.checked);
                                }}
                            />}
                            label={<BoxedIcon label={notification.title}>
                                {icons[notification.name]?.icon}
                            </BoxedIcon>}
                        />
                        <FormHelperText>{notification.description}</FormHelperText>
                    </FormGroup>
                ))}
            </ShadowBGContainer>
            <CustomBR />
            <ShadowBGContainer>
                <Typography color="secondary" fontWeight="bold" variant="h4">
                    <BoxedIcon label="סוגי התראות">
                        <NotificationsNoneOutlined color="primary" sx={{ mr: 1, ml: 1 }} />
                    </BoxedIcon>
                </Typography>
                <CustomBR />
                {formNotificationsRegistrations.map(({ notification, registration }) => (
                    <FormGroup key={notification.notificationid}>
                        <FormControlLabel
                            control={<Switch
                                disabled={!hasAnyActiveChannel}
                                defaultChecked={!!props.activeNotificationSettings?.[notification?.name]?.isActive}
                                {...registration}
                                inputRef={registration.ref}
                                name={notification.name}
                                onChange={e => {
                                    registration.onChange(e);
                                    onChange(notification.notificationid, e.target.checked);
                                }}
                            />}
                            label={<BoxedIcon label={notification.title}>
                                {icons[notification.name]?.icon}
                            </BoxedIcon>}
                        />
                        <FormHelperText>{notification.description}</FormHelperText>
                    </FormGroup>
                ))}
            </ShadowBGContainer></>
    );
};

export default UserNotificationSettings;
